/**
 * CamlinAvatar - High-level wrapper around the core Avatar component
 * Provides a simplified API for embedding in builder/Visual IVR
 */

import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect
} from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF } from '@react-three/drei';
import * as THREE from 'three';

/**
 * Tiny helper component that lives inside the R3F Canvas and forces the
 * renderer + camera to update whenever the parent container changes size.
 * R3F's built-in resize handling (react-use-measure) can miss the initial
 * layout in `position:absolute` containers because the dimensions aren't
 * computed yet when the Canvas first mounts.
 */
function CanvasResizer({ containerSize }: { containerSize: { width: number; height: number } | null }) {
  const { gl, camera, size, invalidate } = useThree();
  void size; // kept in deps: R3F-driven resizes must re-run the check too
  useEffect(() => {
    if (!containerSize || containerSize.width <= 0 || containerSize.height <= 0) return;
    // Compare against the renderer's ACTUAL buffer size, not R3F's `size`
    // state: the manual gl.setSize below bypasses R3F, so `size` goes stale
    // the moment we intervene and can never trigger the next correction.
    const currentSize = gl.getSize(new THREE.Vector2());
    if (Math.abs(currentSize.x - containerSize.width) > 2 || Math.abs(currentSize.y - containerSize.height) > 2) {
      console.log('[CanvasResizer] Fixing size:', currentSize.x, 'x', currentSize.y, '->', containerSize.width, 'x', containerSize.height);
      // updateStyle=false + explicit 100% CSS: only the drawing buffer (and
      // frustum below) follow the measured size — the canvas ELEMENT always
      // fills its container. Passing updateStyle=true here pinned pixel
      // styles onto the canvas from a measurement that can be one frame
      // stale, and R3F's own measure then re-read the pinned canvas —
      // a feedback loop that froze the Companion's face pane at launcher
      // size after a mobile Website|Avatar round-trip (6 Aug 2026).
      gl.setSize(containerSize.width, containerSize.height, false);
      gl.domElement.style.width = '100%';
      gl.domElement.style.height = '100%';
      if ('updateProjectionMatrix' in camera) {
        (camera as THREE.OrthographicCamera).left = -containerSize.width / 2;
        (camera as THREE.OrthographicCamera).right = containerSize.width / 2;
        (camera as THREE.OrthographicCamera).top = containerSize.height / 2;
        (camera as THREE.OrthographicCamera).bottom = -containerSize.height / 2;
        (camera as THREE.OrthographicCamera).updateProjectionMatrix();
      }
      invalidate();
    }
  }, [containerSize, gl, camera, size, invalidate]);
  return null;
}
import type { Sentiment, BlendshapeFrame } from '../types';
import Avatar from '../core/components/Avatar/Avatar';
import { AvatarLoadingSplash } from '../core/components/AvatarLoadingSplash';
import { ErrorBoundary } from '../core/components/ErrorBoundary';
import Scene from '../core/components/Scene/Scene';
import { DebugOverlay, type DebugOverlayConfig } from './DebugOverlay';
import { getModelByUrl, resolveAvatarRuntimeAssetUrl, resolveModelUrl } from '../core/modelRegistry';
import { clearReplayDedupForInstance, replayAvatarRuntimeConfig } from '../core/avatarRuntimeConfig';
import { getSpeechText } from '../core/utils/spokenText';

export type AvatarState = 'idle' | 'listening' | 'thinking' | 'speaking';
export type AvatarMode = 'lite' | 'full';

// Debounce timeout for context loss recovery (ms)
const CONTEXT_LOSS_RECOVERY_DEBOUNCE = 1000;

export interface SpeakOptions {
  ssml?: string;
  voiceId?: string;
  onComplete?: () => void;
}

export interface AvatarModelOption {
  id: string;
  name: string;
  url: string;
  useDefaultMaterials?: boolean;
}

export interface CamlinAvatarConfig {
  mode?: AvatarMode;
  showChat?: boolean;
  showButtons?: boolean;
  enableSentiment?: boolean;
  cameraZoom?: number;
  motionParallaxEnabled?: boolean;
  modelUrl?: string;
  ttsUrl?: string;
  projectToken?: string;
  handoffToken?: string;
  diagramId?: string;
  /** Preload model on mount for faster initial display (default: true) */
  preloadModel?: boolean;
  /** Background color for the canvas */
  backgroundColor?: string;
  /** Show loading indicator */
  showLogo?: boolean;
  /** Skip teeth animation for performance (default: true for Visual IVR) */
  skipTeethAnimation?: boolean;
  /** Show avatar model switcher UI */
  showAvatarSwitcher?: boolean;
  /** Available avatar models for switcher (default: Default and Shenya) */
  avatarModels?: AvatarModelOption[];
  /** Show microphone button for voice input */
  showMicButton?: boolean;
  /** Force the Scene/Avatar stack to apply default material overrides. */
  useDefaultMaterials?: boolean;
  /** Use embedded textures from GLB (skip external texture loading). For models with baked-in textures. */
  useEmbeddedTextures?: boolean;
  /** Appearance applied during initial model setup, merged over model defaults. */
  initialAppearance?: Record<string, string | undefined>;
  /** Full avatar runtime config replayed after model readiness. */
  initialConfig?: Record<string, unknown>;
  /** @deprecated Progressive loading removed. Use HD/SD quality selection instead. */
  lowresModelUrl?: string;
  /** Forward raw Scene voice-call flags when embedding the shared avatar shell into live hosts. */
  isMobileCall?: boolean;
  isWebrtcCall?: boolean;
  isInVoiceCall?: boolean;
  /** Explicitly marks the shared shell as the `/live` experience. */
  isLiveMode?: boolean;
  /**
   * Opt-in flag for snapshot tools (e.g. the admin Portrait Generator).
   * Forwarded to the underlying R3F <Canvas gl={{ preserveDrawingBuffer: true }}>
   * so single-frame canvas.toDataURL() captures succeed instead of
   * returning a blank PNG. See AvatarEmbed.enableCanvasCapture.
   */
  enableCanvasCapture?: boolean;
}

export interface CamlinAvatarHandle {
  speak(text: string, options?: SpeakOptions): void;
  stop(): void;
  setMood(mood: Sentiment, intensity?: number): void;
  setListening(listening: boolean): void;
  setThinking(thinking: boolean): void;
  setIsListening?(listening: boolean): void;
  setIsThinking?(thinking: boolean): void;
  playBlendshapes(frames: BlendshapeFrame[]): void;
  playIdleAnimation?(): void;
  stopAllAnimations?(): void;
  stopVisemeAnimation?(): void;
  getMixer?(): unknown | null;
  getMorphTargetDictionary?(): { body: Record<string, number>; teeth: Record<string, number> } | null;
  getState(): AvatarState;
  isReady(): boolean;
  /**
   * Send a typed/synthetic user turn into an active live contextual-chat
   * session (`visual.input` over the LiveModeClient WebSocket). Optional —
   * only implemented by the live-mode handle (`NativeLiveAvatar`); the
   * full/non-live `CamlinAvatar` handle has no session to send into.
   * Added for Camlin Companion (doc 116 CO1), which drives its own
   * minimal text transcript/input rather than the SDK's built-in chat UI.
   */
  sendText?(value: string, metadata?: Record<string, unknown>): void;
  /**
   * Unlock browser audio playback from a user-gesture context (autoplay
   * policy) and mark prompt playback as unlocked. Optional — live-mode
   * handle only. Added for Camlin Companion's explicit "Enable sound"
   * affordance (Keith, 6 Aug 2026: speech must never play-fail silently).
   */
  unlockAudio?(): Promise<void>;
  /**
   * Start (or stop, when already active) a browser-mic voice turn — the
   * same live-mode primary action the LiveControlDock "Listen" button
   * drives. Optional — live-mode handle only. Hosts that suppress the
   * dock (`hideButtons`/`activeListen: false`) use this to run the
   * canonical mic → STT → transcript path from their own labelled button.
   */
  startVoiceTurn?(): Promise<void>;
  /**
   * Re-run the audio/microphone browser-access flow after the SDK
   * surfaced a permission prompt (see `onBrowserAccessPromptChange`).
   * Must be called from a user gesture. Optional — live-mode handle only.
   */
  retryBrowserAccess?(): Promise<void>;
}

export interface CamlinAvatarProps {
  config?: CamlinAvatarConfig;
  onStateChange?: (state: AvatarState) => void;
  onSpeakStart?: () => void;
  onSpeakComplete?: () => void;
  onMoodChange?: (mood: Sentiment) => void;
  onError?: (error: Error) => void;
  onReady?: () => void;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  orbitControls?: {
    enabled?: boolean;
    enableZoom?: boolean;
    enablePan?: boolean;
    enableDamping?: boolean;
    dampingFactor?: number;
    rotateSpeed?: number;
    target?: [number, number, number];
  };
  debugOverlay?: DebugOverlayConfig;
  onMeshInfo?: (info: { name: string; morphTargetCount: number } | null) => void;
  onMorphTargets?: (targets: string[]) => void;
  /** Called when mic button is clicked (for voice input) */
  onMicClick?: () => void;
  /** Called after default + initial appearance has been applied to the scene. */
  onAppearanceApplied?: () => void;
}

export const CamlinAvatar = forwardRef<CamlinAvatarHandle, CamlinAvatarProps>(
  function CamlinAvatar({ config = {}, onReady, onStateChange, onSpeakStart, onSpeakComplete, onMoodChange, onError, className, style, children, orbitControls, debugOverlay, onMeshInfo, onMorphTargets, onMicClick, onAppearanceApplied }, ref) {
    const avatarRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [state, setState] = useState<AvatarState>('idle');
    const [isReady, setIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [speak, setSpeak] = useState(false);
    const [animateCallSpeak, setAnimateCallSpeak] = useState<any>(null);
    const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null);

    // Model loading / avatar switch tracking (declared before context-loss effect that references it)
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

    // WebGL context loss recovery state
    const [contextLost, setContextLost] = useState(false);
    const [canvasRecoveryKey, setCanvasRecoveryKey] = useState(0);
    const contextLossCountRef = useRef(0);
    const lastContextLossTimeRef = useRef(0);
    const contextRecoveryTimerRef = useRef<number | null>(null);
    const pendingAnimationRef = useRef<any>(null);
    const pendingBlendshapeFramesRef = useRef<BlendshapeFrame[] | null>(null);

    // ResizeObserver to track container size and trigger Canvas resize
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const updateSize = () => {
        // Layout size, NOT getBoundingClientRect: the Companion launcher
        // CSS-scales its stage (transform: scale), and the camera frustum +
        // buffer must follow the LAYOUT box so face framing is
        // scale-invariant — a 240px stage scaled down to a 76px circle must
        // still frame 240px worth of world (the whole head), not 76px (a
        // nose crop). Transforms don't change offsetWidth/offsetHeight.
        const width = container.offsetWidth;
        const height = container.offsetHeight;
        if (width > 0 && height > 0) {
          setContainerSize({ width, height });
        }
      };

      // Initial size check with small delay for layout to settle
      const initialTimer = setTimeout(updateSize, 50);

      // Watch for size changes
      const observer = new ResizeObserver(() => {
        updateSize();
      });
      observer.observe(container);

      return () => {
        clearTimeout(initialTimer);
        observer.disconnect();
      };
    }, []);

    // R3F initial-measure kick. react-use-measure inside <Canvas> can miss the
    // initial layout when the container is position:absolute and gets its real
    // size in the same frame the Canvas mounts (observed on the hosted-site
    // avatar page, Jul 23 2026): the internal ResizeObserver's first delivery
    // reads 0x0, nothing resizes afterwards, so R3F never configures the root —
    // the <canvas> stays at the 300x150 HTML default and the scene (and the
    // avatar:modelReady event) never mounts. When we know the container has a
    // real size but the canvas is still at the default, perturb the R3F wrapper
    // div's width for one frame so the observer fires again with real bounds.
    useEffect(() => {
      if (!containerSize) return;
      const container = containerRef.current;
      if (!container) return;

      let cancelled = false;
      let attempts = 0;
      let timer: number | null = null;

      const kick = () => {
        if (cancelled) return;
        const canvas = container.querySelector('canvas');
        const stuckAtDefault =
          canvas && canvas.width === 300 && canvas.height === 150 &&
          Math.round(containerSize.width) !== 300;
        if (!canvas || !stuckAtDefault) return;
        const wrapper = canvas.parentElement as HTMLElement | null;
        if (!wrapper) return;
        attempts += 1;
        console.log(`[CamlinAvatar] R3F never measured the container — kicking resize (attempt ${attempts})`);
        const prevWidth = wrapper.style.width;
        wrapper.style.width = '99.9%';
        requestAnimationFrame(() => {
          wrapper.style.width = prevWidth || '100%';
        });
        if (attempts < 3) {
          timer = window.setTimeout(kick, 500 * attempts);
        }
      };

      // Give the normal path a beat to succeed before checking.
      timer = window.setTimeout(kick, 250);
      return () => {
        cancelled = true;
        if (timer !== null) window.clearTimeout(timer);
      };
    }, [containerSize, canvasRecoveryKey]);

    // WebGL context loss recovery handler
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const handleContextLost = (event: Event) => {
        event.preventDefault(); // Allow context to be restored
        const now = Date.now();
        const rapidLoss = now - lastContextLossTimeRef.current < CONTEXT_LOSS_RECOVERY_DEBOUNCE;
        contextLossCountRef.current = rapidLoss ? contextLossCountRef.current + 1 : 1;

        if (animateCallSpeak) {
          pendingAnimationRef.current = animateCallSpeak;
        }
        setAnimateCallSpeak(null);
        setSpeak(false);
        setContextLost(true);
        setIsLoading(true);
        setHasLoadedOnce(false);
        setIsReady(false);
        onReadyCalledRef.current = false;

        // Track rapid context losses
        if (rapidLoss) {
          console.warn(`[CamlinAvatar] 🔴 WebGL context lost (${contextLossCountRef.current}x in rapid succession) - pausing animations`);
        } else {
          console.warn('[CamlinAvatar] 🔴 WebGL context lost - will attempt recovery');
        }

        // Mirror onto the window so `useAvatarHealth` can flip the
        // status pip to red without coupling to the canvas ref. Same
        // mirror pattern used by AvatarSceneShell (the runtime-host
        // alternative shell). Both shells emit the same event so a
        // consumer subscribing to `avatar:webgl-context-lost` works
        // regardless of which shell is mounted.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('avatar:webgl-context-lost', {
              detail: { count: contextLossCountRef.current, rapid: rapidLoss }
            })
          );
        }

        if (contextRecoveryTimerRef.current !== null) {
          window.clearTimeout(contextRecoveryTimerRef.current);
        }
        contextRecoveryTimerRef.current = window.setTimeout(() => {
          contextRecoveryTimerRef.current = null;
          console.warn('[CamlinAvatar] Recreating Canvas after context loss');
          setContextLost(false);
          setCanvasRecoveryKey((current) => current + 1);
        }, rapidLoss ? 240 : 120);

        lastContextLossTimeRef.current = now;
      };

      const handleContextRestored = () => {
        console.log('[CamlinAvatar] 🟢 WebGL context restored');
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('avatar:webgl-context-restored'));
        }
        if (contextRecoveryTimerRef.current !== null) {
          window.clearTimeout(contextRecoveryTimerRef.current);
          contextRecoveryTimerRef.current = null;
        }
        setContextLost(false);
        setIsLoading(true);
        // Don't immediately restore animation - wait for model ready event
      };

      // Listen for canvas context events
      const observer = new MutationObserver(() => {
        const canvas = container.querySelector('canvas');
        if (canvas) {
          canvas.addEventListener('webglcontextlost', handleContextLost);
          canvas.addEventListener('webglcontextrestored', handleContextRestored);
        }
      });

      observer.observe(container, { childList: true, subtree: true });

      // Also check for existing canvas
      const existingCanvas = container.querySelector('canvas');
      if (existingCanvas) {
        existingCanvas.addEventListener('webglcontextlost', handleContextLost);
        existingCanvas.addEventListener('webglcontextrestored', handleContextRestored);
      }

      return () => {
        observer.disconnect();
        if (contextRecoveryTimerRef.current !== null) {
          window.clearTimeout(contextRecoveryTimerRef.current);
          contextRecoveryTimerRef.current = null;
        }
        const canvas = container.querySelector('canvas');
        if (canvas) {
          canvas.removeEventListener('webglcontextlost', handleContextLost);
          canvas.removeEventListener('webglcontextrestored', handleContextRestored);
        }
      };
    }, [animateCallSpeak]);

    const mode = config.mode || 'full';
    const shouldPreload = config.preloadModel !== false; // Default true
    const onReadyCalledRef = useRef(false);

    // Avatar model switcher
    const defaultModels: AvatarModelOption[] = [
      { id: 'default', name: 'Arc (Default)', url: '/models/arc.glb' },
      { id: 'sam', name: 'Sam', url: '/model.glb' },
      { id: 'shenya', name: 'Shenya', url: '/shenya-fullbody-ue.glb', useDefaultMaterials: true },
    ];
    const availableModels = config.avatarModels || defaultModels;
    const [selectedModelIndex, setSelectedModelIndex] = useState(0);
    const selectedModel = availableModels[selectedModelIndex];
    // If switcher is enabled, use the selected model URL. Otherwise use config.modelUrl if provided.
    // Apply resolveModelUrl to ensure relative URLs are converted to CDN URLs
    const rawModelUrl = config.showAvatarSwitcher ? selectedModel.url : (config.modelUrl || selectedModel.url);
    const fullResModelUrl = resolveModelUrl(rawModelUrl);
    const [modelLoadFallbackUrl, setModelLoadFallbackUrl] = useState<string | null>(null);
    const modelUrl = modelLoadFallbackUrl ?? fullResModelUrl;
    const isBundledAvatarModel =
      rawModelUrl === '/model.glb'
      || rawModelUrl === '/shenya-fullbody-ue.glb'
      || modelUrl.endsWith('/model.glb')
      || modelUrl.endsWith('/shenya-fullbody-ue.glb');
    const avatarInstanceIdRef = useRef(`camlin-avatar-${Math.random().toString(36).slice(2)}`);
    const mountTimeRef = useRef(Date.now());

    useEffect(() => {
      setModelLoadFallbackUrl(null);
    }, [fullResModelUrl]);

    // Track model URL changes to reset loading state for avatar switches.
    const prevFullResRef = useRef<string>(fullResModelUrl);
    if (prevFullResRef.current !== fullResModelUrl) {
      prevFullResRef.current = fullResModelUrl;
      onReadyCalledRef.current = false;
      if (!isLoading) {
        setIsLoading(true);
      }
      mountTimeRef.current = Date.now();
    }

    // Determine useDefaultMaterials based on the actual model being loaded
    // If modelUrl matches a known model with useDefaultMaterials, use that.
    // Otherwise, if modelUrl is a non-default model (like shenya), enable it.
    const getUseDefaultMaterials = (): boolean => {
      if (config.showAvatarSwitcher) {
        return selectedModel.useDefaultMaterials ?? false;
      }
      // Check if modelUrl matches a known model in availableModels
      const matchingModel = availableModels.find(m =>
        resolveModelUrl(m.url) === modelUrl
        || m.url === rawModelUrl
        || (m as any).lowresUrl === rawModelUrl
        || ((m as any).lowresUrl ? resolveModelUrl((m as any).lowresUrl) === modelUrl : false)
      );
      if (matchingModel) {
        return matchingModel.useDefaultMaterials ?? false;
      }
      // For custom modelUrls, check if it's a shenya variant
      if (modelUrl && modelUrl.toLowerCase().includes('shenya')) {
        return true;
      }
      return false;
    };
    const useDefaultMaterials = config.useDefaultMaterials ?? getUseDefaultMaterials();

    // Determine useEmbeddedTextures: explicitly set, or auto-detect for non-default, non-shenya models
    const useEmbeddedTextures = config.useEmbeddedTextures ??
      (!isBundledAvatarModel && !modelUrl?.toLowerCase().includes('shenya') && !useDefaultMaterials);

    const handleModelLoadError = useCallback((error: Error) => {
      const matchingModel = getModelByUrl(modelUrl);
      const highResUrl = matchingModel ? resolveModelUrl(matchingModel.url) : null;
      const canFallbackToHighRes = Boolean(
        highResUrl &&
        highResUrl !== modelUrl &&
        matchingModel?.lowresUrl &&
        resolveModelUrl(matchingModel.lowresUrl) === modelUrl
      );

      if (canFallbackToHighRes && highResUrl) {
        console.warn('[CamlinAvatar] Low-res model failed; retrying with full-res model', {
          failedUrl: modelUrl,
          fallbackUrl: highResUrl,
          error
        });
        setModelLoadFallbackUrl(highResUrl);
        return;
      }

      onError?.(error);
    }, [modelUrl, onError]);

    // Warm the browser HTTP cache for the model URL on mount.
    // Using fetch() instead of useGLTF.preload() to avoid crashes from
    // Three.js GLTFLoader's synchronous onError callback.
    useEffect(() => {
      if (!shouldPreload || !modelUrl) return;
      if (isBundledAvatarModel) {
        // Local models don't need HTTP cache warming
        return;
      }
      console.log('[CamlinAvatar] Preloading model:', modelUrl);
      fetch(modelUrl).then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.arrayBuffer();
      }).then(buf => {
        console.log(`[CamlinAvatar] Model preloaded: ${(buf.byteLength / (1024 * 1024)).toFixed(1)}MB`);
      }).catch((err: unknown) => {
        console.warn('[CamlinAvatar] Model preload error (non-fatal):', err);
      });
    }, [shouldPreload, modelUrl, isBundledAvatarModel]);

    const replayPendingBlendshapes = useCallback((attempt = 0) => {
      const frames = pendingBlendshapeFramesRef.current;
      if (!frames?.length) {
        return;
      }

      if (!avatarRef.current?.isReady?.()) {
        if (attempt < 20) {
          window.setTimeout(() => replayPendingBlendshapes(attempt + 1), 50);
        }
        return;
      }

      pendingBlendshapeFramesRef.current = null;
      const blendData = frames.map(f => ({ time: f.time, blendshapes: f.values }));
      setAnimateCallSpeak({ blendData });
      setState('speaking');
      onStateChange?.('speaking');
    }, [onStateChange]);

    // Listen for model ready event from Avatar component
    // This fires AFTER the model is fully loaded and morphTargetDictionary is set
    // Use a layout effect so cached models cannot dispatch the ready event
    // before the listener is attached on the initial mount.
    useLayoutEffect(() => {
      const handleModelReady = () => {
        if (onReadyCalledRef.current) return;
        onReadyCalledRef.current = true;
        console.log(`[CamlinAvatar] Model ready in ${((Date.now() - mountTimeRef.current) / 1000).toFixed(1)}s`);
        setIsLoading(false);
        setHasLoadedOnce(true);
        setIsReady(true);
        setContextLost(false);

        // Reset context loss counter on successful initialization
        contextLossCountRef.current = 0;

        // Don't restore pending animation if there were rapid context losses
        // This prevents the loop from restarting
        if (pendingAnimationRef.current && contextLossCountRef.current === 0) {
          console.log('[CamlinAvatar] Context stable - pending animation cleared (user can re-trigger)');
          pendingAnimationRef.current = null;
        }

        replayPendingBlendshapes();

        onReady?.();
      };

      window.addEventListener('avatar:modelReady', handleModelReady);
      return () => {
        window.removeEventListener('avatar:modelReady', handleModelReady);
      };
    }, [onReady, replayPendingBlendshapes]);

    useEffect(() => {
      if (!isReady || !config.initialConfig) {
        return;
      }

      replayAvatarRuntimeConfig(config.initialConfig, { avatarInstanceId: avatarInstanceIdRef.current });
    }, [config.initialConfig, isReady]);

    // Drop the dedup snapshot for this instance on unmount so a future
    // remount with the same (or different) config gets a fresh
    // dispatch. Without this the dedup cache would slowly grow across
    // long sessions that mount/unmount avatars repeatedly (the portrait
    // generator, the cross-channel simulator, etc.). The 32-entry cap
    // inside `avatarRuntimeConfig.ts` is a backstop; this is the
    // intended cleanup path.
    useEffect(() => {
      const instanceId = avatarInstanceIdRef.current;
      return () => {
        clearReplayDedupForInstance(instanceId);
      };
    }, []);

    useImperativeHandle(ref, () => ({
      async speak(text: string, options?: SpeakOptions) {
        // Guard: Don't call TTS if context is lost or not ready
        if (contextLost) {
          console.warn('[CamlinAvatar] Skipping TTS - WebGL context lost');
          return;
        }

        if (!isReady) {
          console.warn('[CamlinAvatar] Skipping TTS - avatar not ready yet');
          return;
        }

        // Call TTS if configured
        if (config.ttsUrl) {
          try {
            const speechText = getSpeechText(text);
            console.log('[CamlinAvatar] Calling TTS:', `${config.ttsUrl}/talk`, 'text:', speechText.substring(0, 30) + '...');
            const response = await fetch(`${config.ttsUrl}/talk`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({ text: speechText, voice: options?.voiceId }),
              mode: 'cors',
              credentials: 'omit'
            });

            console.log('[CamlinAvatar] TTS response:', response.status, response.statusText);

            if (response.ok) {
              const data = await response.json();
              console.log('[CamlinAvatar] TTS data received:', {
                hasAudioData: !!data.audioData,
                hasBlendData: !!data.blendData,
                blendDataLength: data.blendData?.length
              });

              if (data.blendData) {
                setAnimateCallSpeak({ blendData: data.blendData });
              }

              // Play audio if available
              if (data.audioData) {
                const audio = new Audio(`data:audio/mpeg;base64,${data.audioData}`);
                audio.onended = () => {
                  setState('idle');
                  onSpeakComplete?.();
                };
                audio.onerror = (err) => {
                  console.error('[CamlinAvatar] Audio playback error:', err);
                };
                await audio.play();
              }

              setState('speaking');
              onSpeakStart?.();
            } else {
              const errorText = await response.text();
              console.error('[CamlinAvatar] TTS request failed:', response.status, errorText);
              throw new Error(`TTS failed: ${response.status} ${errorText}`);
            }
          } catch (error) {
            console.error('[CamlinAvatar] TTS error:', error);
            console.error('[CamlinAvatar] Error details:', {
              message: (error as Error)?.message,
              name: (error as Error)?.name,
              stack: (error as Error)?.stack
            });
            onError?.(error instanceof Error ? error : new Error(String(error)));
          }
        } else {
          console.warn('[CamlinAvatar] No ttsUrl configured, cannot speak');
        }
      },

      stop() {
        setSpeak(false);
        setAnimateCallSpeak(null);
        avatarRef.current?.stopAllAnimations?.();
        setState('idle');
        onStateChange?.('idle');
      },

      setMood(mood: Sentiment, intensity?: number) {
        window.dispatchEvent(new CustomEvent('avatar:mood:preview', {
          detail: { mood, intensity }
        }));
        onMoodChange?.(mood);
      },

      setListening(listening: boolean) {
        avatarRef.current?.setIsListening?.(listening);
        setState(listening ? 'listening' : 'idle');
        onStateChange?.(listening ? 'listening' : 'idle');
      },

      setThinking(thinking: boolean) {
        avatarRef.current?.setIsThinking?.(thinking);
        setState(thinking ? 'thinking' : 'idle');
        onStateChange?.(thinking ? 'thinking' : 'idle');
      },

      setIsListening(listening: boolean) {
        avatarRef.current?.setIsListening?.(listening);
        setState(listening ? 'listening' : 'idle');
        onStateChange?.(listening ? 'listening' : 'idle');
      },

      setIsThinking(thinking: boolean) {
        avatarRef.current?.setIsThinking?.(thinking);
        setState(thinking ? 'thinking' : 'idle');
        onStateChange?.(thinking ? 'thinking' : 'idle');
      },

      playBlendshapes(frames: BlendshapeFrame[]) {
        if (!avatarRef.current?.isReady?.()) {
          pendingBlendshapeFramesRef.current = frames;
          replayPendingBlendshapes();
          return;
        }

        // Convert to expected format
        const blendData = frames.map(f => ({ time: f.time, blendshapes: f.values }));
        setAnimateCallSpeak({ blendData });
        setState('speaking');
        onStateChange?.('speaking');
      },

      playIdleAnimation() {
        avatarRef.current?.playIdleAnimation?.();
        setState('idle');
        onStateChange?.('idle');
      },

      stopAllAnimations() {
        avatarRef.current?.stopAllAnimations?.();
        setSpeak(false);
        setAnimateCallSpeak(null);
        setState('idle');
        onStateChange?.('idle');
      },

      stopVisemeAnimation() {
        avatarRef.current?.stopVisemeAnimation?.();
        setSpeak(false);
        setAnimateCallSpeak(null);
        setState('idle');
        onStateChange?.('idle');
      },

      getMixer() {
        return avatarRef.current?.getMixer?.() ?? null;
      },

      getMorphTargetDictionary() {
        return avatarRef.current?.getMorphTargetDictionary?.() ?? null;
      },

      getState() {
        return state;
      },

      isReady() {
        return isReady;
      }
    }), [state, isReady, contextLost, config.ttsUrl, onStateChange, onSpeakStart, onMoodChange, onError]);

    if (mode !== 'full') {
      return (
        <div className={className} style={style}>
          <div>Lite mode not implemented - use mode: 'full'</div>
        </div>
      );
    }

    return (
      <div ref={containerRef} className={className} style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        ...style
      }}>
        {isLoading && (
          <AvatarLoadingSplash
            avatarName={getModelByUrl(modelUrl)?.name}
            contained
            portraitUrl={
              getModelByUrl(modelUrl)?.portrait
                ? resolveModelUrl(getModelByUrl(modelUrl)!.portrait!)
                : getModelByUrl(modelUrl)?.thumbnail
                  ? resolveModelUrl(getModelByUrl(modelUrl)!.thumbnail!)
                  : null
            }
          />
        )}
        <Canvas
          key={canvasRecoveryKey}
          dpr={2}
          // Measure by LAYOUT size (offsetWidth), not transformed bounds —
          // must agree with the container ResizeObserver above. The
          // Companion launcher CSS-scales a 240px stage into a 76px circle;
          // if R3F measured the transformed rect it would build the camera
          // frustum for 76px of world (nose crop) and fight the
          // CanvasResizer's layout-based sizing.
          resize={{ offsetSize: true }}
          // Snapshot tools (admin Portrait Generator) need
          // `preserveDrawingBuffer: true` so they can call
          // `canvas.toDataURL(...)` after the avatar is ready.
          // Default WebGLRenderer behaviour clears the framebuffer
          // between frames, which makes single-frame captures return a
          // blank PNG. We only enable it when the host explicitly
          // opts in via config.enableCanvasCapture so normal embed/live
          // usage doesn't pay the per-frame copy cost.
          gl={config.enableCanvasCapture ? { preserveDrawingBuffer: true } : undefined}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          onCreated={(ctx) => {
            const gl = ctx.gl as any;
            // Match standalone App.tsx renderer settings for visual parity
            gl.physicallyCorrectLights = true;
            gl.toneMapping = THREE.ACESFilmicToneMapping;
            gl.toneMappingExposure = 0.72;
            if ('outputColorSpace' in gl) {
              gl.outputColorSpace = THREE.SRGBColorSpace;
            } else {
              gl.outputEncoding = 3001;
            }

            console.log('🎨 [SDK Avatar] WebGL Renderer initialized:', {
              threeVersion: (THREE as any).REVISION,
              toneMapping: gl.toneMapping,
              toneMappingExposure: gl.toneMappingExposure,
              outputEncoding: gl.outputEncoding,
              outputColorSpace: gl.outputColorSpace,
              physicallyCorrectLights: gl.physicallyCorrectLights
            });
            // NOTE: onReady is now called via avatar:modelReady event
            // This ensures the model is fully loaded before signaling ready
          }}
        >
          {/* Fix initial size when parent layout hasn't settled yet */}
          <CanvasResizer containerSize={containerSize} />
          <ErrorBoundary
            key={modelUrl}
            fallback={null}
            onError={handleModelLoadError}
          >
            <Scene
              avatarRef={avatarRef}
              userTalking={false}
              speak={contextLost ? false : speak}
              setSpeak={setSpeak}
              setRecord={() => { }}
              text=""
              setAudioSource={() => { }}
              animateCallSpeak={contextLost ? null : animateCallSpeak}
              setAnimateCallSpeak={setAnimateCallSpeak}
              setPlaying={() => { }}
              playing={false}
              deviceOrientation={{ alpha: 0, beta: 45, gamma: 0 }}
              orientationPermission="denied"
              animateHangupButton={() => { }}
              resetHangupButton={() => { }}
              isMobileCall={config.isMobileCall ?? false}
              isWebrtcCall={config.isWebrtcCall ?? false}
              isInVoiceCall={config.isInVoiceCall ?? false}
              isLiveMode={config.isLiveMode ?? false}
              cameraZoom={config.cameraZoom}
              modelUrl={modelUrl}
              modelKey={modelUrl}
              skipTeethAnimation={config.skipTeethAnimation ?? true}
              useDefaultMaterials={useDefaultMaterials}
              useEmbeddedTextures={useEmbeddedTextures}
              initialAppearance={config.initialAppearance}
              avatarInstanceId={avatarInstanceIdRef.current}
              onAppearanceApplied={onAppearanceApplied}
            />
          </ErrorBoundary>
          {orbitControls?.enabled && (
            <OrbitControls
              enableZoom={orbitControls.enableZoom ?? true}
              enablePan={orbitControls.enablePan ?? true}
              enableDamping={orbitControls.enableDamping ?? true}
              dampingFactor={orbitControls.dampingFactor ?? 0.1}
              rotateSpeed={orbitControls.rotateSpeed ?? 0.6}
              target={orbitControls.target ?? [0, 1.4, 0]}
            />
          )}
          {debugOverlay && (
            <DebugOverlay
              config={debugOverlay}
              onMeshInfo={onMeshInfo}
              onMorphTargets={onMorphTargets}
            />
          )}
          {children}
        </Canvas>

        {/* Avatar Model Switcher */}
        {config.showAvatarSwitcher && availableModels.length > 1 && (
          <div style={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            background: 'rgba(0,0,0,0.6)',
            borderRadius: '20px',
            color: 'white',
            fontSize: '12px',
            backdropFilter: 'blur(10px)',
            zIndex: 9999, // High z-index to appear above Visual IVR content
            pointerEvents: 'auto',
          }}>
            <button
              onClick={() => setSelectedModelIndex((prev) => (prev - 1 + availableModels.length) % availableModels.length)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '14px',
              }}
              title="Previous avatar"
            >
              ◀
            </button>
            <span style={{ minWidth: '60px', textAlign: 'center' }}>
              {selectedModel.name}
            </span>
            <button
              onClick={() => setSelectedModelIndex((prev) => (prev + 1) % availableModels.length)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '14px',
              }}
              title="Next avatar"
            >
              ▶
            </button>
          </div>
        )}

        {/* Microphone Button for Voice Input */}
        {config.showMicButton && onMicClick && (
          <button
            onClick={onMicClick}
            style={{
              position: 'absolute',
              bottom: '20px',
              right: '20px',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '24px',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              zIndex: 9999,
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
            }}
            title="Tap to speak"
          >
            🎤
          </button>
        )}
      </div>
    );
  }
);

/**
 * Preload the avatar model before rendering
 * Call this early in your app to ensure the model is ready when the avatar mounts
 * @param modelUrl URL to the GLB model (default: '/model.glb')
 */
export function preloadAvatarModel(modelUrl: string = '/model.glb'): void {
  const resolvedModelUrl = resolveModelUrl(modelUrl);
  const dracoDecoderPath = resolveAvatarRuntimeAssetUrl('/draco/');
  console.log('[CamlinAvatar] Static preload:', resolvedModelUrl);
  try {
    const p = useGLTF.preload(resolvedModelUrl, dracoDecoderPath);
    if (p && typeof (p as Promise<unknown>).catch === 'function') {
      (p as Promise<unknown>).catch((err: unknown) => {
        console.warn('[CamlinAvatar] Static preload error (non-fatal):', err);
      });
    }
  } catch (err) {
    console.warn('[CamlinAvatar] Static preload error (non-fatal):', err);
  }
}

export default CamlinAvatar;
